import { useState, useEffect, useCallback } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  Search, SlidersHorizontal, X, ArrowUpDown, LayoutGrid, List,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';
import { Product, Category, SUBCATEGORY_LABELS } from '../data/products';
import { resellerProductsApi, ProductsQueryParams, transformResellerProduct } from '../api/resellerProducts';
import ProductCard from '../components/ProductCard';

function mapSortToApi(sort: string): { sort_by: string; sort_order: string } {
  switch (sort) {
    case 'priceAsc': return { sort_by: 'price', sort_order: 'asc' };
    case 'priceDesc': return { sort_by: 'price', sort_order: 'desc' };
    case 'nameAz': return { sort_by: 'name', sort_order: 'asc' };
    case 'stock': return { sort_by: 'stock', sort_order: 'desc' };
    default: return { sort_by: 'name', sort_order: 'asc' };
  }
}

export default function ResellerCatalogue() {
  usePageTitle("Catalogue revendeur — Lodiss");

  const { lang, catalogueFilter, setCatalogueFilter } = useApp();
  const T = t[lang];

  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('nameAz');
  const [filters, setFilters] = useState<any>({
    categories: [],
    subcategories: [],
    sectors: [],
    brands: [],
    norms: [],
    priceMin: '',
    priceMax: '',
    stockOnly: false,
    purchaseMode: 'all',
  });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const handleFiltersChange = (f: any) => {
    setFilters(f);
    if (f.categories.length === 1) {
      setCatalogueFilter(f.categories[0]);
    } else {
      setCatalogueFilter('all');
    }
  };

  const buildQueryParams = useCallback((): ProductsQueryParams => {
    const params: ProductsQueryParams = {
      ...mapSortToApi(sortBy),
      limit: 100,
    };

    if (filters.categories.length > 0) {
      params.category = filters.categories[0];
    }
    if (filters.subcategories.length > 0) {
      params.subcategory = filters.subcategories[0];
    }
    if (filters.sectors.length > 0) {
      params.sector = filters.sectors[0];
    }
    if (filters.brands.length > 0) {
      params.brand = filters.brands[0];
    }
    if (filters.norms.length > 0) {
      params.norm = filters.norms[0];
    }
    if (filters.priceMin) {
      params.price_min = parseFloat(filters.priceMin);
    }
    if (filters.priceMax) {
      params.price_max = parseFloat(filters.priceMax);
    }
    if (filters.stockOnly) {
      params.stock_only = true;
    }
    if (filters.purchaseMode !== 'all') {
      params.purchase_mode = filters.purchaseMode;
    }
    if (search.trim()) {
      params.search = search.trim();
    }

    return params;
  }, [filters, search, sortBy]);

  useEffect(() => {
    setLoading(true);
    setError(null);

    resellerProductsApi.getProducts(buildQueryParams())
      .then(data => {
        setProducts(data.products.map(transformResellerProduct));
        setTotal(data.total);
      })
      .catch(err => {
        console.error('Failed to fetch products:', err);
        setError('Failed to load products');
      })
      .finally(() => setLoading(false));
  }, [buildQueryParams]);

  return (
    <main className="bg-white min-h-screen">
      {/* Top toolbar */}
      <div className="border-b border-gray-100 bg-white sticky top-[calc(2.5rem+4rem)] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={T.common.search}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-8 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-lodiss-red focus:ring-1 focus:ring-lodiss-red/20 transition-colors"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X size={13} />
                </button>
              )}
            </div>

            <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
              <ArrowUpDown size={13} className="text-gray-400" />
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:border-lodiss-red bg-white text-gray-700"
              >
                <option value="nameAz">{T.common.nameAz}</option>
                <option value="priceAsc">{T.common.priceAsc}</option>
                <option value="priceDesc">{T.common.priceDesc}</option>
                <option value="stock">{lang === 'fr' ? 'Stock décroissant' : 'Highest stock'}</option>
              </select>
            </div>

            <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <LayoutGrid size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <List size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-950">
            {lang === 'fr' ? 'Catalogue Revendeur' : 'Reseller Catalogue'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {lang === 'fr' ? 'Prix de gros exclusive' : 'Wholesale pricing'}
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="animate-pulse">
                <div className="aspect-square bg-gray-200 rounded-xl mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/3"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="text-lodiss-red font-semibold hover:underline"
            >
              {lang === 'fr' ? 'Réessayer' : 'Retry'}
            </button>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {lang === 'fr' ? 'Aucun produit trouvé' : 'No products found'}
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-4">
              {total} {lang === 'fr' ? 'produits' : 'products'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {products.map(p => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
